import {
  Article,
  Demo,
  GlossaryPage,
  GlossaryTerm,
  GuideMainTab,
  GuideSubTab,
  SearchStrapiResponse,
  StrapiResponse,
  SubTab,
  Tutorial,
} from '@app/components/Guide/types/guide.types'
import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'

export const fetchAllTabs = async (): Promise<GuideMainTab[]> => {
  const response = await apiClient.get(Endpoint.getAllGuideTabs)
  return response.data.data
}

export const fetchSubBySlug = async (subTopicSlug: string): Promise<SubTab> => {
  const response = await apiClient.get(
    `/cms/sub-tabs?filters[slug][$eq]=${subTopicSlug}` +
      `&fields[0]=title` +
      `&fields[1]=slug` +
      `&populate[main_topic][fields][0]=title` +
      `&populate[main_topic][fields][1]=slug` +
      `&populate[main_topic][fields][2]=description` +
      `&populate[articles][fields][0]=title` +
      `&populate[articles][fields][1]=slug` +
      `&populate[articles][fields][2]=description` +
      `&populate[articles][fields][3]=is_external` +
      `&populate[articles][fields][4]=external_link` +
      `&populate[articles][populate][hero_image][fields][0]=url` +
      `&populate[articles][populate][hero_image][fields][1]=alternativeText`,
  )

  return response.data.data[0]
}

export interface ArticleResponse extends StrapiResponse {
  data: Article[]
}

export interface DemoResponse extends StrapiResponse {
  data: Demo[]
}

export interface TutorialResponse extends StrapiResponse {
  data: Tutorial[]
}

export const fetchArticleBySlug = async (slug: string): Promise<ArticleResponse> => {
  const response = await apiClient.get(`/cms/articles?filters[slug][$eq]=${slug}&populate=*`)
  return response.data
}

export const fetchSubTabsByMainTopic = async (mainTopicSlug: string): Promise<GuideSubTab[]> => {
  const slug = encodeURIComponent(mainTopicSlug)
  const response = await apiClient.get(
    `/cms/sub-tabs?populate[main_topic][fields][0]=title&populate[main_topic][fields][1]=slug&filters[main_topic][slug][$eq]=${slug}&sort=order:asc`,
  )
  return response.data.data
}

export const fetchAllSubTabs = async (): Promise<GuideSubTab[]> => {
  const res = await apiClient.get(
    `/cms/sub-tabs` +
      `?fields[0]=title&fields[1]=slug&fields[2]=order&fields[3]=visible` +
      `&populate[main_topic][fields][0]=title&populate[main_topic][fields][1]=slug` +
      `&sort=order:asc`,
  )
  return res.data.data
}

export const fetchDemoBySlug = async (slug: string): Promise<DemoResponse['data'][0]> => {
  const response = await apiClient.get(`/cms/demos?filters[Slug][$eq]=${slug}`)
  return response.data.data[0]
}

export const fetchTutorialBySlug = async (slug: string): Promise<TutorialResponse['data'][0]> => {
  const response = await apiClient.get(`/cms/tutorials?filters[slug][$eq]=${slug}`)
  return response.data.data[0]
}

export const fetchDemosBySubTabSlug = async (): Promise<DemoResponse['data']> => {
  const response = await apiClient.get(
    `/cms/demos?populate[thumbnail][fields][0]=url&populate[sub_tabs][populate]=*`,
  )
  return response.data.data
}
export const fetchTutorialsBySubTabSlug = async (
  subTopic: string,
): Promise<TutorialResponse['data']> => {
  const response = await apiClient.get(
    `/cms/tutorials?filters[sub_tabs][slug][$eq]=${subTopic}&populate[thumbnail][fields][0]=url&populate[sub_tabs][populate]=*`,
  )
  return response.data.data
}

export const fetchGlossary = async (): Promise<{ Content?: string }> => {
  const response = await apiClient.get(`/cms/glossary`)
  return response.data.data
}

export const fetchSearchResult = async (query: string): Promise<SearchStrapiResponse> => {
  const q = query.trim().replace(/\\+$/, '')

  const response = await apiClient.get('/cms/articles', {
    params: {
      filters: {
        $or: [
          { title: { $contains: q } },
          { description: { $contains: q } },
          { slug: { $contains: q } },
        ],
      },
      sort: ['publishedAt:desc'],
      pagination: {
        limit: 10,
      },
      populate: {
        sub_tabs: {
          populate: {
            main_topic: true,
          },
        },
      },
    },
  })

  return response.data
}

export const fetchGlossaryPage = async (): Promise<GlossaryPage> => {
  const response = await apiClient.get('/cms/glossary-page')
  return response.data.data
}

export const fetchGlossaryTerms = async (): Promise<GlossaryTerm[]> => {
  const response = await apiClient.get(
    '/cms/glossary-terms?sort=letter:asc,term:asc&pagination[page]=1&pagination[pageSize]=200', // NOTICE: this bypass default strapi pagination limit of 25
  )

  return response.data.data
}

export const fetchGlossarySingleTerm = async (slug: string): Promise<GlossaryTerm | null> => {
  const response = await apiClient.get(`/cms/glossary-terms?filters[slug][$eq]=${slug}`)

  const items = response.data.data

  return items.length > 0 ? items[0] : null
}
